import { Component, Vue, Watch } from 'vue-property-decorator';
import UserService from '@/services/user.service';
import InputPassword from '@/components/inputs/InputPassword/InputPassword';
import LocalStoreService from '@/store/local.store';
import { PasswordValidationResult, PasswordValidationFailure } from '@/models/PasswordValidationResult';


@Component({
  components: {
    InputPassword,
  },
})
export default class PasswordModification extends Vue {
  // Properties
  public errorMessage: string = '';
  public successMessage: string = '';
  private userName: string =  '';
  private oldPassword: string = '';
  private newPassword: string = '';
  private repeatNewPassword: string = '';
  
  // Password validation
  private passwordValidationResult: PasswordValidationResult | null = null;
  private isValidatingPassword: boolean = false;

  // Lifecycle hook
  public mounted() {
    this.userName = LocalStoreService.getItem(LocalStoreService.localItemKeys.proUsername) || '';
  }

  // Watch for password changes
  @Watch('newPassword')
  private async onNewPasswordChanged(newValue: string): Promise<void> {
    if (!newValue || newValue.length === 0) {
      this.passwordValidationResult = null;
      return;
    }

    this.isValidatingPassword = true;
    this.passwordValidationResult = await UserService.validatePassword(newValue);
    this.isValidatingPassword = false;
  }

  // Computed properties for password strength
  get passwordStrengthColor(): string {
    if (!this.passwordValidationResult) return 'grey';
    const score = this.passwordValidationResult.score;
    if (score < 30) return 'red';
    if (score < 60) return 'orange';
    if (score < 80) return 'amber';
    return 'green';
  }

  get passwordStrengthLabel(): string {
    if (!this.passwordValidationResult) return '';
    const score = this.passwordValidationResult.score;
    if (score < 30) return this.$t('password.validation.weak') as string;
    if (score < 60) return this.$t('password.validation.medium') as string;
    if (score < 80) return this.$t('password.validation.good') as string;
    return this.$t('password.validation.strong') as string;
  }

  get passwordsMatch(): boolean {
    return this.newPassword === this.repeatNewPassword && this.newPassword.length > 0;
  }

  get isPasswordValid(): boolean {
    return this.passwordValidationResult !== null 
      && this.passwordValidationResult.isValid 
      && this.passwordsMatch
      && this.oldPassword.length > 0;
  }

  // Get failure messages for display
  public getFailureMessages(): string[] {
    if (!this.passwordValidationResult || !this.passwordValidationResult.failures) {
      return [];
    }

    const errorKeys: { [key: string]: string } = {
      'TooShort': 'tooShort',
      'TooLong': 'tooLong',
      'MissingDigit': 'missingDigit',
      'MissingUppercase': 'missingUppercase',
      'MissingLowercase': 'missingLowercase',
      'MissingSpecialCharacter': 'missingSpecialCharacter',
      'NotEnoughUniqueCharacters': 'notEnoughUniqueCharacters',
      'CommonPassword': 'commonPassword',
      'SequentialPattern': 'sequentialPattern',
      'RepetitivePattern': 'repetitivePattern'
    };

    return this.passwordValidationResult.failures
      .map((failure: PasswordValidationFailure) => {
        const key = errorKeys[failure];
        if (key) {
          return this.$t(`password.validation.errors.${key}`) as string;
        }
        return '';
      })
      .filter((msg: string) => msg !== '');
  }

  /**
   * Saves the newly entered password.
   */
  protected async saveNewPassword() {
    this.errorMessage = '';

    if (this.newPassword !== this.repeatNewPassword) {
      this.errorMessage = 'errorMgs.password.newPasswordsNotMatching';
      return;
    }

    await UserService.saveNewPassword(this.userName, this.oldPassword, this.newPassword)
      .then(() => {
        this.successMessage = 'password.successfullyChanged';
      })
      .catch((error: any) => {

        if (error.response === undefined) {
          return;
        }

        switch (error.response.status) {
          case 401:
            this.errorMessage = 'errorMgs.password.oldPasswordNotMatching';
            break;
          case 404:
            this.errorMessage = 'errorMgs.password.usernameUnknown';
            break;
          default:
            this.errorMessage = 'errorMgs.password.savingPasswordError';
        }
      });
  }

}
